import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanDeactivateFn } from '@angular/router';

import { Observable, of, of as observableOf } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { DialogService } from '../../dialogs/dialogs.service';
import { ProtectedControlsDialogComponent } from '../../dialogs/protected-controls-dialog/protected-controls-dialog.component';
import { ProtectedControlMeta } from './protected-control.directive';
import { ProtectedControlsService } from './protected-controls.service';

/**
 * If navigation is redirected canDeactivate can be called multiple times, so we need to save
 * information that user allowed to leave the page to avoid multiple ProtectedControls dialog openings
 * for each redirect.
 */

const deactivatedSnapshotsMap = new WeakMap<ActivatedRouteSnapshot, boolean>();

/**
 * @deprecated
 * Use `protectedControlsGuardFn` instead
 */
@Injectable({ providedIn: 'root' })
export class ProtectedControlsGuardService {
  private protectedControls = inject(ProtectedControlsService);
  private dialogService = inject(DialogService);

  public canDeactivate(
    _cmpInstance: any,
    currentSnapshot: ActivatedRouteSnapshot,
  ): Observable<boolean> {
    const { locked, reports } = this.protectedControls;
    const isDeactivated = !!deactivatedSnapshotsMap.get(currentSnapshot);

    return protectedControlsRoutedStrategy(
      this.dialogService,
      locked,
      isDeactivated,
      reports,
      currentSnapshot,
    );
  }
}

export const protectedControlsGuardFn: CanDeactivateFn<any> = (
  _cmpInstance: any,
  currentSnapshot: ActivatedRouteSnapshot,
) => {
  const dialogService = inject(DialogService);
  const protectedControls = inject(ProtectedControlsService);

  const { locked, reports } = protectedControls;
  const isDeactivated = !!deactivatedSnapshotsMap.get(currentSnapshot);

  return protectedControlsRoutedStrategy(
    dialogService,
    locked,
    isDeactivated,
    reports,
    currentSnapshot,
  );
};

export const useProtectedControlsCallback = () => {
  const protectedControls = inject(ProtectedControlsService);
  const dialogService = inject(DialogService);

  return <T extends () => any, R = ReturnType<T>>(cb: T): Observable<R> => {
    const { locked, reports } = protectedControls;
    return protectedControlsCallbackStrategy(dialogService, locked, reports, cb);
  };
};

const protectedControlsCallbackStrategy = (
  dialogService: DialogService,
  locked: boolean,
  reports: ProtectedControlMeta[],
  cb: () => any,
) =>
  shouldDisplayDialog(locked, reports)
    ? displayDialog(dialogService).pipe(map(result => (result ? cb() : null)))
    : of(cb());

const protectedControlsRoutedStrategy = (
  dialogService: DialogService,
  locked: boolean,
  isDeactivated: boolean,
  reports: ProtectedControlMeta[],
  currentSnapshot: ActivatedRouteSnapshot,
) =>
  !isDeactivated && shouldDisplayDialog(locked, reports)
    ? displayDialog(dialogService).pipe(
        tap(result => {
          if (result) {
            deactivatedSnapshotsMap.set(currentSnapshot, true);
          }
        }),
      )
    : observableOf(true);

const shouldDisplayDialog = (locked: boolean, reports: ProtectedControlMeta[]) =>
  locked &&
  !reports.every(report => report.name === '<p><br /></p>' && report.newValue === '<p><br /></p>');

const displayDialog = (svc: DialogService) =>
  svc.openCustom(ProtectedControlsDialogComponent, {}, {}).afterClosed.pipe(
    map(result => !!result),
    first(),
  );
